import React, { useEffect, useMemo } from 'react';
import { Button, Form, Modal, TimePicker } from '@maxtropy/components';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { format } from '../utils';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { apiV2PeriodDetailUpdatePost } from '@maxtropy/device-customer-apis-v2';

interface Props {
  currentDate: number;
  periodList: any[];
  modalVisible?: boolean;
  setModalVisible: (v: boolean) => void;
  forceUpdate?: () => void;
}

const EditPeriodModal: React.FC<Props> = ({ currentDate, periodList, modalVisible, setModalVisible, forceUpdate }) => {
  const [form] = Form.useForm();
  const { id } = useParams();

  useEffect(() => {
    form.setFieldValue(
      'timeList',
      periodList.map(item => ({
        startTime: dayjs(item.startTime, 'HH:mm'),
        endTime: dayjs(item.endTime, 'HH:mm'),
      }))
    );
  }, [periodList, currentDate]);

  const onOk = () => {
    form.validateFields().then(values => {
      if (!id) return;
      apiV2PeriodDetailUpdatePost({
        currentDay: currentDate,
        dataQueryPeriodId: id,
        timeList: values.timeList.map((m: any) => ({
          startTime: dayjs(m.startTime).format('HH:mm'),
          endTime: dayjs(m.endTime).format('HH:mm'),
        })),
      }).then(res => {
        setModalVisible(false);
        forceUpdate?.();
      });
    });
  };

  const onCancel = () => {
    form.resetFields();
    setModalVisible?.(false);
  };

  return (
    <Modal
      contentClassName="modal-form-content"
      open={modalVisible}
      bodyScroll
      title="编辑时段"
      width={720}
      onCancel={onCancel}
      onOk={onOk}
      destroyOnClose
    >
      <Form
        form={form}
        initialValues={{
          ruleData: [
            {
              endIndex: undefined,
              endTime: undefined,
            },
          ],
        }}
      >
        <Form.Item label="时间">
          <Form.List name="timeList">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name }, index) => (
                  <div key={index} className={styles.periodRow}>
                    <Form.Item
                      name={[name, 'startTime']}
                      rules={[
                        {
                          validator(_, value) {
                            const timeList = form.getFieldValue('timeList') || [];
                            const current = timeList[index];

                            if (!value || !current?.endTime) return Promise.resolve();

                            const start = dayjs(value, 'HH:mm');
                            const end = dayjs(current.endTime, 'HH:mm');

                            const adjustedEnd = end.format('HH:mm') === '00:00' ? end.add(1, 'day') : end;

                            if (!start.isBefore(adjustedEnd)) {
                              return Promise.reject(new Error('开始时间必须早于结束时间'));
                            }

                            for (let i = 0; i < timeList.length; i++) {
                              if (i === index) continue;
                              const other = timeList[i];
                              if (!other?.startTime || !other?.endTime) continue;

                              const otherStart = dayjs(other.startTime, 'HH:mm');
                              const otherEndRaw = dayjs(other.endTime, 'HH:mm');
                              const otherEnd =
                                otherEndRaw.format('HH:mm') === '00:00' ? otherEndRaw.add(1, 'day') : otherEndRaw;

                              const overlap = start.isBefore(otherEnd) && adjustedEnd.isAfter(otherStart);
                              if (overlap) {
                                return Promise.reject(new Error('时段与其他时段有重叠'));
                              }
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <TimePicker style={{ width: 200 }} placeholder="请选择" format={format} showNow={false} />
                    </Form.Item>
                    <div>-</div>
                    <Form.Item
                      name={[name, 'endTime']}
                      rules={[
                        {
                          validator(_, value) {
                            const timeList = form.getFieldValue('timeList') || [];
                            const current = timeList[index];

                            if (!value || !current?.startTime) return Promise.resolve();

                            const start = dayjs(current.startTime, 'HH:mm');
                            const end = dayjs(value, 'HH:mm');
                            const adjustedEnd = end.format('HH:mm') === '00:00' ? end.add(1, 'day') : end;

                            if (!start.isBefore(adjustedEnd)) {
                              return Promise.reject(new Error('结束时间必须晚于开始时间'));
                            }

                            for (let i = 0; i < timeList.length; i++) {
                              if (i === index) continue;
                              const other = timeList[i];
                              if (!other?.startTime || !other?.endTime) continue;

                              const otherStart = dayjs(other.startTime, 'HH:mm');
                              const otherEndRaw = dayjs(other.endTime, 'HH:mm');
                              const otherEnd =
                                otherEndRaw.format('HH:mm') === '00:00' ? otherEndRaw.add(1, 'day') : otherEndRaw;

                              const overlap = start.isBefore(otherEnd) && adjustedEnd.isAfter(otherStart);
                              if (overlap) {
                                return Promise.reject(new Error('时段与其他时段有重叠'));
                              }
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <TimePicker style={{ width: 200 }} placeholder="请选择" format={format} showNow={false} />
                    </Form.Item>
                    <MinusCircleOutlined style={{ fontSize: 16, color: '#4A90E2' }} onClick={() => remove(name)} />
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    wrapStyle={{ width: 426 }}
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    className={styles.btn}
                    ghost
                  >
                    新增时段
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditPeriodModal;
